import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  DevicesOther as DevicesIcon,
  PowerSettingsNew as RevokeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Kiosk, KioskStatus, deriveStatus, kioskService } from '../services/kioskService';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
  70% { box-shadow: 0 0 0 8px transparent; opacity: 0.85; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
`;

const statusConfig: Record<KioskStatus, { label: string; color: string; dot: string; animate: boolean }> = {
  online: { label: 'Online', color: '#2E7D32', dot: '#48BB78', animate: true },
  idle: { label: 'Idle', color: '#B7791F', dot: '#ED8936', animate: false },
  offline: { label: 'Offline', color: '#4A5568', dot: '#A0AEC0', animate: false },
  revoked: { label: 'Revoked', color: '#9B2C2C', dot: '#F56565', animate: false },
};

const StatusDot: React.FC<{ status: KioskStatus }> = ({ status }) => {
  const cfg = statusConfig[status];
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: cfg.dot,
        color: cfg.dot,
        display: 'inline-block',
        animation: cfg.animate ? `${pulse} 2s infinite` : 'none',
      }}
    />
  );
};

const StatusChip: React.FC<{ status: KioskStatus }> = ({ status }) => {
  const cfg = statusConfig[status];
  return (
    <Chip
      size="small"
      icon={<StatusDot status={status} />}
      label={cfg.label}
      sx={{
        bgcolor: alpha(cfg.dot, 0.15),
        color: cfg.color,
        fontWeight: 600,
        '& .MuiChip-icon': { ml: 1, mr: -0.5 },
      }}
    />
  );
};

const relativeTime = (iso: string | null, now: number): string => {
  if (!iso) return '—';
  const diff = now - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const countdown = (expiresAt: string, now: number): { mmss: string; pct: number; expired: boolean } => {
  const end = new Date(expiresAt).getTime();
  const remaining = Math.max(0, end - now);
  const total = 10 * 60 * 1000;
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return {
    mmss: `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
    pct: Math.min(100, (remaining / total) * 100),
    expired: remaining === 0,
  };
};

const EmptyState: React.FC<{ onPair: () => void }> = ({ onPair }) => (
  <Paper
    sx={{
      p: 6,
      textAlign: 'center',
      border: '2px dashed',
      borderColor: 'divider',
      bgcolor: 'transparent',
      boxShadow: 'none',
    }}
  >
    <DevicesIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      No kiosks paired yet
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 440, mx: 'auto' }}>
      Pair an in-salon AI Assistant tablet to this salon. Generate a one-time code here and enter it on
      the kiosk — no logins, no manual tenant setup.
    </Typography>
    <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={onPair}>
      Pair your first kiosk
    </Button>
  </Paper>
);

const PairingDialog: React.FC<{
  open: boolean;
  code: string | null;
  expiresAt: string | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onRegenerate: () => void;
  onCopy: () => void;
}> = ({ open, code, expiresAt, loading, error, onClose, onRegenerate, onCopy }) => {
  const theme = useTheme();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  const timer = expiresAt ? countdown(expiresAt, now) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pair a new kiosk</DialogTitle>
      <DialogContent>
        {loading && !code && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Generating pairing code…
            </Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {code && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter this code on the kiosk's pairing screen. Codes expire in 10 minutes.
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderColor: alpha(theme.palette.primary.main, 0.3),
                position: 'relative',
              }}
            >
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  color: 'primary.dark',
                  userSelect: 'all',
                }}
              >
                {code}
              </Typography>
              <Tooltip title="Copy to clipboard">
                <IconButton
                  onClick={onCopy}
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>

            {timer && (
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {timer.expired ? 'Expired' : 'Expires in'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: timer.expired ? 'error.main' : timer.pct < 20 ? 'warning.main' : 'text.secondary',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {timer.mmss}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={timer.pct}
                  color={timer.expired ? 'error' : timer.pct < 20 ? 'warning' : 'primary'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              How to pair
            </Typography>
            <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, color: 'text.secondary', '& li': { typography: 'body2' } }}>
              <li>Open the Salon AI Assistant app on the kiosk.</li>
              <li>On first launch, tap <strong>Pair this kiosk</strong>.</li>
              <li>Enter the code above and give the kiosk a name (e.g. "Reception iPad").</li>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {timer?.expired && (
          <Button startIcon={<RefreshIcon />} onClick={onRegenerate} disabled={loading}>
            Generate new code
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfirmRevoke: React.FC<{
  kiosk: Kiosk | null;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
}> = ({ kiosk, onCancel, onConfirm, pending }) => (
  <Dialog open={!!kiosk} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>Revoke kiosk?</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">
        <strong>{kiosk?.name}</strong> will immediately lose access. The kiosk will need to be paired
        again before it can talk to your salon.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained" disabled={pending}>
        {pending ? 'Revoking…' : 'Revoke'}
      </Button>
    </DialogActions>
  </Dialog>
);

const Kiosks: React.FC = () => {
  const queryClient = useQueryClient();
  const [pairOpen, setPairOpen] = useState(false);
  const [pairingCode, setPairingCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [pairError, setPairError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [confirmKiosk, setConfirmKiosk] = useState<Kiosk | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(id);
  }, []);

  const { data: kiosks, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['kiosks'],
    queryFn: kioskService.list,
    refetchInterval: 30000,
  });

  const generateMutation = useMutation({
    mutationFn: kioskService.generatePairingCode,
    onSuccess: (data) => {
      setPairingCode(data);
      setPairError(null);
    },
    onError: (err: Error) => {
      setPairError(err.message || 'Could not generate a pairing code');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: kioskService.revoke,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosks'] });
      setToast({ msg: 'Kiosk revoked', severity: 'success' });
      setConfirmKiosk(null);
    },
    onError: (err: Error) => {
      setToast({ msg: err.message || 'Revoke failed', severity: 'error' });
    },
  });

  const openPair = () => {
    setPairingCode(null);
    setPairError(null);
    setPairOpen(true);
    generateMutation.mutate();
  };

  const closePair = () => {
    setPairOpen(false);
    setPairingCode(null);
    queryClient.invalidateQueries({ queryKey: ['kiosks'] });
  };

  const copyCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode.code);
      setToast({ msg: 'Copied to clipboard', severity: 'success' });
    } catch {
      setToast({ msg: 'Copy failed — select the code and press ⌘C', severity: 'error' });
    }
  };

  const activeKiosks = kiosks?.filter((k) => k.isActive) ?? [];
  const onlineCount = useMemo(
    () => activeKiosks.filter((k) => deriveStatus(k, now) === 'online').length,
    [activeKiosks, now]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Kiosks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            In-salon AI Assistant tablets. Pair new devices and monitor their status here.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openPair}>
            Pair new kiosk
          </Button>
        </Stack>
      </Stack>

      {kiosks && kiosks.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Active kiosks
              </Typography>
              <Typography variant="h4">{activeKiosks.length}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Online now
              </Typography>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4">{onlineCount}</Typography>
                <Typography variant="body2" color="text.secondary">
                  / {activeKiosks.length}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {isLoading && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        </Paper>
      )}

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load kiosks.
        </Alert>
      )}

      {!isLoading && !isError && (kiosks?.length ?? 0) === 0 && <EmptyState onPair={openPair} />}

      {!isLoading && (kiosks?.length ?? 0) > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kiosk</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell>Paired</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kiosks!.map((k) => {
                const status = deriveStatus(k, now);
                return (
                  <TableRow
                    key={k.id}
                    hover
                    sx={{
                      opacity: k.isActive ? 1 : 0.55,
                      '& td': { py: 1.75 },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: 'primary.light',
                            color: 'primary.dark',
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          <DevicesIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {k.name}
                          </Typography>
                          {k.deviceId && (
                            <Typography variant="caption" color="text.secondary">
                              {k.deviceId}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{relativeTime(k.lastHeartbeat, now)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {relativeTime(k.pairedAt, now)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {k.isActive ? (
                        <Tooltip title="Revoke kiosk access">
                          <IconButton size="small" onClick={() => setConfirmKiosk(k)}>
                            <RevokeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip size="small" icon={<CheckIcon />} label="Revoked" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PairingDialog
        open={pairOpen}
        code={pairingCode?.code ?? null}
        expiresAt={pairingCode?.expiresAt ?? null}
        loading={generateMutation.isPending}
        error={pairError}
        onClose={closePair}
        onRegenerate={() => generateMutation.mutate()}
        onCopy={copyCode}
      />

      <ConfirmRevoke
        kiosk={confirmKiosk}
        onCancel={() => setConfirmKiosk(null)}
        onConfirm={() => confirmKiosk && revokeMutation.mutate(confirmKiosk.id)}
        pending={revokeMutation.isPending}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Kiosks;
